import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button,
  Autocomplete,
  IconButton,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../services/api";
import { todayISO } from "../utils/date";

/* ================= MAIN ================= */
export default function RegradeMaterials() {
  const [editId, setEditId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <Box sx={{ width: "100%" }}>
      <RegradeEditor editId={editId} setEditId={setEditId} onSaved={() => setReloadKey((k) => k + 1)} />
      <RegradeList key={reloadKey} onEdit={setEditId} />
    </Box>
  );
}

/* ================= EDITOR ================= */
function RegradeEditor({ editId, setEditId, onSaved }) {
  const [products, setProducts] = useState([]);

  const today = todayISO();

  const emptyMaster = useMemo(() => ({
    Master_ID: null,
    Date: today,
    Source_Item_ID: null,
    Source_Item_Description: "",
    Source_Qty: 0,
    Notes: "",
  }), [today]);

  const emptyDetail = useMemo(() => ({
    Item_ID: null,
    Item_Description: "",
    Qty: 0,
  }), []);

  const [master, setMaster] = useState(emptyMaster);
  const [details, setDetails] = useState([{ ...emptyDetail }]);

  const resetForm = useCallback(() => {
    setMaster(emptyMaster);
    setDetails([{ ...emptyDetail }]);
  }, [emptyMaster, emptyDetail]);

  /* === Load products === */
  useEffect(() => {
    api.get("/regrade/products")
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load products:", e));
  }, []);

  /* === Load record for edit === */
  useEffect(() => {
    if (!editId) return;
    api.get(`/regrade/${editId}`)
      .then((res) => {
        const m = res.data?.master || {};
        const d = res.data?.details || [];
        setMaster({
          Master_ID: m.Master_ID || null,
          Date: m.Date ? String(m.Date).split("T")[0] : today,
          Source_Item_ID: m.Source_Item_ID || null,
          Source_Item_Description: m.Source_Item_Description || "",
          Source_Qty: Number(m.Source_Qty) || 0,
          Notes: m.Notes || "",
        });
        setDetails(d.length ? d.map((row) => ({
          Item_ID: row.Item_ID || null,
          Item_Description: row.Item_Description || "",
          Qty: Number(row.Qty) || 0,
        })) : [{ ...emptyDetail }]);
      })
      .catch((err) => {
        console.error("Failed to load regrade:", err);
        resetForm();
      });
  }, [editId, today, emptyDetail, resetForm]);

  /* === Detail row helpers === */
  const updateRow = (i, field, value) => {
    const rows = [...details];
    if (field === "Item") {
      rows[i].Item_ID = value?.id || null;
      rows[i].Item_Description = value?.Product_Description || "";
    } else if (field === "Qty") {
      rows[i].Qty = value === "" ? 0 : Number(value);
    }
    setDetails(rows);
  };
  const addRow = () => setDetails((prev) => [...prev, { ...emptyDetail }]);
  const removeRow = (i) => setDetails((prev) => {
    const next = prev.filter((_, idx) => idx !== i);
    return next.length ? next : [{ ...emptyDetail }];
  });

  /* === Totals === */
  const totalDest = details.reduce((s, r) => s + (Number(r.Qty) || 0), 0);
  const sourceQty = Number(master.Source_Qty) || 0;
  const diff = +(sourceQty - totalDest).toFixed(2);
  const overAllocated = totalDest > sourceQty + 0.001;

  /* === Save === */
  const saveRegrade = async () => {
    if (!master.Source_Item_ID) {
      alert("Pick a source material first");
      return;
    }
    if (sourceQty <= 0) {
      alert("Source quantity must be greater than zero");
      return;
    }
    const filled = details.filter((d) => d.Item_ID && Number(d.Qty) > 0);
    if (!filled.length) {
      alert("Add at least one destination material with a quantity");
      return;
    }
    if (overAllocated) {
      const ok = window.confirm(
        `Destination total (${totalDest}) exceeds source quantity (${sourceQty}). Save anyway?`
      );
      if (!ok) return;
    }

    try {
      const payload = { master, details: filled };
      const res = editId
        ? await api.put(`/regrade/${editId}`, payload)
        : await api.post("/regrade", payload);

      alert(res.data?.message || "Saved");
      resetForm();
      setEditId(null);
      onSaved?.();
    } catch (err) {
      console.error("Save regrade failed:", err);
      alert("Failed to save: " + (err.response?.data?.error || err.message));
    }
  };

  const cancelEdit = () => {
    resetForm();
    setEditId(null);
  };

  return (
    <Box p={1}>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6">{editId ? "Edit Regrade" : "Regrade Materials"}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Split one source material into one or more destination materials after sorting.
        </Typography>

        {/* Master row */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              type="date"
              label="Date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={master.Date || ""}
              onChange={(e) => setMaster({ ...master, Date: e.target.value })}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 8, md: 6 }}>
            <Autocomplete
              options={products}
              getOptionLabel={(o) => o?.Product_Description || ""}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={products.find((p) => p.id === master.Source_Item_ID) || null}
              onChange={(e, v) =>
                setMaster({
                  ...master,
                  Source_Item_ID: v?.id || null,
                  Source_Item_Description: v?.Product_Description || "",
                })
              }
              renderInput={(params) => <TextField {...params} label="Source Material" fullWidth />}
              fullWidth
              sx={{ width: "100%", minWidth: { xs: "100%", sm: 280 } }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <TextField
              type="number"
              label="Source Quantity"
              fullWidth
              value={master.Source_Qty}
              onChange={(e) => setMaster({ ...master, Source_Qty: e.target.value })}
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notes (optional)"
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              value={master.Notes || ""}
              onChange={(e) => setMaster({ ...master, Notes: e.target.value })}
            />
          </Grid>
        </Grid>

        {/* Destinations table */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
          Destination Materials
        </Typography>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "70%" }}>Material</TableCell>
                <TableCell sx={{ width: "20%" }}>Qty</TableCell>
                <TableCell sx={{ width: "10%" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {details.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Autocomplete
                      options={products}
                      getOptionLabel={(o) => o?.Product_Description || ""}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      value={products.find((p) => p.id === r.Item_ID) || null}
                      onChange={(e, v) => updateRow(i, "Item", v)}
                      renderInput={(params) => <TextField {...params} size="small" placeholder="Pick material" />}
                      sx={{ width: "100%", minWidth: 200 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={r.Qty}
                      onChange={(e) => updateRow(i, "Qty", e.target.value)}
                      inputProps={{ step: "0.01", min: "0" }}
                      sx={{ width: "100%", minWidth: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton color="error" size="small" onClick={() => removeRow(i)} aria-label="remove row">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 1 }}>
          <Button onClick={addRow}>+ Add Destination</Button>
        </Box>

        {/* Totals strip */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1,
            bgcolor: overAllocated ? "error.light" : "action.hover",
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Source</Typography>
            <Typography sx={{ fontWeight: 600 }}>{sourceQty.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Allocated</Typography>
            <Typography sx={{ fontWeight: 600 }}>{totalDest.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {diff >= 0 ? "Loss / Unallocated" : "Over-allocated"}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: overAllocated ? "error.dark" : "inherit" }}>
              {Math.abs(diff).toFixed(2)}
            </Typography>
          </Box>
          {overAllocated && (
            <Chip color="error" size="small" label="Total exceeds source" sx={{ ml: "auto" }} />
          )}
        </Box>

        <Box mt={2}>
          <Button variant="contained" onClick={saveRegrade}>{editId ? "Update" : "Save"}</Button>
          {editId && <Button sx={{ ml: 2 }} onClick={cancelEdit}>Cancel</Button>}
        </Box>
      </Paper>
    </Box>
  );
}

/* ================= LIST ================= */
function RegradeList({ onEdit }) {
  const today = todayISO();
  const sevenDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(sevenDaysAgo);
  const [toDate, setToDate] = useState(today);

  const fetchRows = useCallback(() => {
    api.get("/regrade", { params: { name: search, fromDate, toDate } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load regrades:", e));
  }, [search, fromDate, toDate]);

  useEffect(() => fetchRows(), [fetchRows]);

  const del = async (id) => {
    if (!window.confirm("Delete this regrade?")) return;
    try {
      await api.delete(`/regrade/${id}`);
      setRows((prev) => prev.filter((x) => x.Master_ID !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box p={1}>
      <Typography variant="h6" sx={{ mb: 1 }}>Past Regrades</Typography>

      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField label="Material" fullWidth value={search} onChange={(e) => setSearch(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 3 }}>
            <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 2 }}>
            <Button variant="contained" fullWidth onClick={fetchRows}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Source Material</TableCell>
              <TableCell>Source Qty</TableCell>
              <TableCell>Allocated</TableCell>
              <TableCell>Loss</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: "text.secondary" }}>
                  No regrades in this date range
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const src = Number(r.Source_Qty) || 0;
                const alloc = Number(r.TotalDestQty) || 0;
                const loss = +(src - alloc).toFixed(2);
                return (
                  <TableRow key={r.Master_ID}>
                    <TableCell>{r.Master_ID}</TableCell>
                    <TableCell>{r.Date ? String(r.Date).split("T")[0] : "-"}</TableCell>
                    <TableCell>{r.Source_Item_Description}</TableCell>
                    <TableCell>{src.toFixed(2)}</TableCell>
                    <TableCell>{alloc.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: loss < 0 ? "error.main" : "inherit" }}>
                      {loss.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => onEdit(r.Master_ID)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => del(r.Master_ID)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
